import { Trans, useLingui } from '@lingui/react/macro'
import { Card, Group, Stack, Text, ThemeIcon } from '@mantine/core'
import type { MaintenanceLogWithRelations } from '@markku/types'
import { IconCheck, IconCircle } from '@tabler/icons-react'

interface CompletedStepsCardProps {
  log: MaintenanceLogWithRelations
}

export function CompletedStepsCard({ log }: CompletedStepsCardProps) {
  const { t } = useLingui()
  const steps = log.steps || []

  if (steps.length === 0) {
    return null
  }

  return (
    <Card withBorder>
      <Text size="lg" fw={600} mb="md">
        <Trans>Completed steps</Trans>
      </Text>

      <Stack gap="md">
        {steps.map((step, index) => {
          const stepName =
            step.stepNameSnapshot ||
            step.maintenanceTaskStep?.name ||
            t`Step ${index + 1}`
          const stepDescription =
            step.descriptionSnapshot || step.maintenanceTaskStep?.description
          const isCompleted = step.status === 'completed'

          return (
            <Group key={step.id} align="flex-start" wrap="nowrap" gap="sm">
              <ThemeIcon
                color={isCompleted ? 'green' : 'gray'}
                variant="light"
                size="md"
                radius="xl"
                style={{ marginTop: 2 }}
              >
                {isCompleted ? (
                  <IconCheck size={16} />
                ) : (
                  <IconCircle size={16} />
                )}
              </ThemeIcon>

              <Stack gap={4} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  {stepName}
                </Text>
                {stepDescription && (
                  <Text size="sm" c="dimmed">
                    {stepDescription}
                  </Text>
                )}
                {step.notes && (
                  <Text size="sm" c="dimmed" fs="italic">
                    <Trans>Note: {step.notes}</Trans>
                  </Text>
                )}
              </Stack>
            </Group>
          )
        })}
      </Stack>
    </Card>
  )
}
